import type { FeAnagrafica_FeAnagrafiche, FeFatturaSavedData_FeFatturaSavedAddressBook } from '../dbs.js';
import type { ProbablyString } from './types.js';

//universal anagrafica handler for both regular invoices and isAutoFattura
//in regular mode invoice owner is "CEDENTE / PRESTATORE" and other party is "CESSIONARIO / COMMITTENTE"
//in isAutoFattura mode (autofattura) invoice owner is "CESSIONARIO / COMMITTENTE"
//and other party is "CEDENTE / PRESTATORE" (owner sends invoice to himself on behalf of other party)
//we need to properly handle anagrafica initialization, address book selection and switch from two modes
//owner - person who creates invoice
//otherparty - other party, may be "CEDENTE / PRESTATORE" in case of autofattura

/*
<xs:complexType name="CedentePrestatoreType">
  <xs:sequence>
    <xs:element name="DatiAnagrafici"             type="DatiAnagraficiCedenteType"               />
*    <xs:element name="Sede"                       type="IndirizzoType"                           />
*--    <xs:element name="StabileOrganizzazione"      type="IndirizzoType"             minOccurs="0" />
    <xs:element name="IscrizioneREA"              type="IscrizioneREAType"         minOccurs="0" />
    <xs:element name="Contatti"                   type="ContattiType"              minOccurs="0" />
    <xs:element name="RiferimentoAmministrazione" type="String20Type"              minOccurs="0" />
  </xs:sequence>
</xs:complexType>
<xs:complexType name="CessionarioCommittenteType">
  <xs:sequence>
    <xs:element name="DatiAnagrafici"        type="DatiAnagraficiCessionarioType"                      />
*    <xs:element name="Sede"                  type="IndirizzoType"                                      />
*--    <xs:element name="StabileOrganizzazione" type="IndirizzoType"                        minOccurs="0" />
 --    <xs:element name="RappresentanteFiscale" type="RappresentanteFiscaleCessionarioType" minOccurs="0" />
  </xs:sequence>
</xs:complexType>

<xs:complexType name="DatiAnagraficiCedenteType">
  <xs:sequence>
*    <xs:element name="IdFiscaleIVA"         type="IdFiscaleTypeNoCheck"                   />
*    <xs:element name="CodiceFiscale"        type="CodiceFiscaleTypeNoCheck" minOccurs="0" />
*    <xs:element name="Anagrafica"           type="AnagraficaType"                  />
    <xs:element name="AlboProfessionale"    type="String60LatinType" minOccurs="0" />
    <xs:element name="ProvinciaAlbo"        type="ProvinciaType"     minOccurs="0" />
    <xs:element name="NumeroIscrizioneAlbo" type="String60Type"      minOccurs="0" />
    <xs:element name="DataIscrizioneAlbo"   type="xs:date"           minOccurs="0" />
    <xs:element name="RegimeFiscale"        type="RegimeFiscaleType"               />
  </xs:sequence>
</xs:complexType>
<xs:complexType name="DatiAnagraficiCessionarioType">
  <xs:sequence>
*    <xs:element name="IdFiscaleIVA"  type="IdFiscaleTypeNoCheck"     minOccurs="0" />
*    <xs:element name="CodiceFiscale" type="CodiceFiscaleTypeNoCheck" minOccurs="0" />
*    <xs:element name="Anagrafica"    type="AnagraficaType"                  />
  </xs:sequence>
</xs:complexType>

extended fields:
'EsigibilitaIVA',
'ModalitaPagamento',
'IstitutoFinanziario',
'IBAN',
'TipoRitenuta',
'AliquotaRitenuta',
'ImponibileRitenutaPercentuale',
'CausalePagamento',
'TipoRitenuta2',
'AliquotaRitenuta2',
'ImponibileRitenutaPercentuale2',
'CausalePagamento2',
'TipoCassa',
'AlCassa'
*/

type AnagraficaTableRow = {
    ctl_src: string;
    ctl_dest: string;
    addr_book: (keyof FeFatturaSavedData_FeFatturaSavedAddressBook) | '';
    anag: (keyof FeAnagrafica_FeAnagrafiche) | '';
};

const _ANAGRAFICA_TABLE = {
    //DatiAnagrafici
    //IdFiscaleIVA
    DatiAnagrafici_IdPaese: {
        ctl_src: '1_2_1_1_1_IdPaese',
        ctl_dest: '1_4_1_1_1_IdPaese',
        addr_book: 'DatiAnagrafici_IdPaese',
        anag: 'IdPaese',
    },
    DatiAnagrafici_IdCodice: {
        ctl_src: '1_2_1_1_2_IdCodice',
        ctl_dest: '1_4_1_1_2_IdCodice',
        addr_book: 'DatiAnagrafici_IdCodice',
        anag: 'IdCodice',
    },
    //CodiceFiscale
    DatiAnagrafici_CodiceFiscale: {
        ctl_src: '1_2_1_2_CodiceFiscale',
        ctl_dest: '1_4_1_2_CodiceFiscale',
        addr_book: 'DatiAnagrafici_CodiceFiscale',
        anag: 'CodiceFiscale',
    },
    //Anagrafica
    DatiAnagrafici_Denominazione: {
        ctl_src: '1_2_1_3_1_Denominazione',
        ctl_dest: '1_4_1_3_1_Denominazione',
        addr_book: 'DatiAnagrafici_Denominazione',
        anag: 'Denominazione',
    },
    DatiAnagrafici_Nome: {
        ctl_src: '1_2_1_3_2_Nome',
        ctl_dest: '1_4_1_3_2_Nome',
        addr_book: 'DatiAnagrafici_Nome',
        anag: 'Nome',
    },
    DatiAnagrafici_Cognome: {
        ctl_src: '1_2_1_3_3_Cognome',
        ctl_dest: '1_4_1_3_3_Cognome',
        addr_book: 'DatiAnagrafici_Cognome',
        anag: 'Cognome',
    },
    DatiAnagrafici_Titolo: {
        ctl_src: '1_2_1_3_4_Titolo',
        ctl_dest: '1_4_1_3_4_Titolo',
        addr_book: 'DatiAnagrafici_Titolo',
        anag: 'Titolo',
    },
    DatiAnagrafici_CodEORI: {
        ctl_src: '1_2_1_3_5_CodEORI',
        ctl_dest: '1_4_1_3_5_CodEORI',
        addr_book: 'DatiAnagrafici_CodEORI',
        anag: 'CodEORI',
    },
    //only DatiAnagraficiCedenteType
    DatiAnagrafici_AlboProfessionale: {
        ctl_src: '1_2_1_4_AlboProfessionale',
        ctl_dest: '',
        addr_book: '',
        anag: 'AlboProfessionale',
    },
    DatiAnagrafici_ProvinciaAlbo: {
        ctl_src: '1_2_1_5_ProvinciaAlbo',
        ctl_dest: '',
        addr_book: '',
        anag: 'ProvinciaAlbo',
    },
    DatiAnagrafici_NumeroIscrizioneAlbo: {
        ctl_src: '1_2_1_6_NumeroIscrizioneAlbo',
        ctl_dest: '',
        addr_book: '',
        anag: 'NumeroIscrizioneAlbo',
    },
    DatiAnagrafici_DataIscrizioneAlbo: {
        ctl_src: '1_2_1_7_DataIscrizioneAlbo',
        ctl_dest: '',
        addr_book: '',
        anag: 'DataIscrizioneAlbo',
    },
    DatiAnagrafici_RegimeFiscale: {
        ctl_src: '1_2_1_8_RegimeFiscale',
        ctl_dest: '',
        addr_book: '',
        anag: 'RegimeFiscale',
    },

    //Sede
    Sede_Indirizzo: {
        ctl_src: '1_2_2_1_Indirizzo',
        ctl_dest: '1_4_2_1_Indirizzo',
        addr_book: 'Sede_Indirizzo',
        anag: 'Indirizzo',
    },
    Sede_NumeroCivico: {
        ctl_src: '1_2_2_2_NumeroCivico',
        ctl_dest: '1_4_2_2_NumeroCivico',
        addr_book: 'Sede_NumeroCivico',
        anag: 'NumeroCivico',
    },
    Sede_CAP: {
        ctl_src: '1_2_2_3_CAP',
        ctl_dest: '1_4_2_3_CAP',
        addr_book: 'Sede_CAP',
        anag: 'CAP',
    },
    Sede_Comune: {
        ctl_src: '1_2_2_4_Comune',
        ctl_dest: '1_4_2_4_Comune',
        addr_book: 'Sede_Comune',
        anag: 'Comune',
    },
    Sede_Provincia: {
        ctl_src: '1_2_2_5_Provincia',
        ctl_dest: '1_4_2_5_Provincia',
        addr_book: 'Sede_Provincia',
        anag: 'Provincia',
    },
    Sede_Nazione: {
        ctl_src: '1_2_2_6_Nazione',
        ctl_dest: '1_4_2_6_Nazione',
        addr_book: 'Sede_Nazione',
        anag: 'Nazione',
    },

    //only DatiAnagraficiCedenteType
    IscrizioneREA_Ufficio: {
        ctl_src: '1_2_4_1_Ufficio',
        ctl_dest: '',
        addr_book: '',
        anag: 'Ufficio',
    },
    IscrizioneREA_NumeroREA: {
        ctl_src: '1_2_4_2_NumeroREA',
        ctl_dest: '',
        addr_book: '',
        anag: 'NumeroREA',
    },
    IscrizioneREA_CapitaleSociale: {
        ctl_src: '1_2_4_3_CapitaleSociale',
        ctl_dest: '',
        addr_book: '',
        anag: 'CapitaleSociale',
    },
    IscrizioneREA_SocioUnico: {
        ctl_src: '1_2_4_4_SocioUnico',
        ctl_dest: '',
        addr_book: '',
        anag: 'SocioUnico',
    },
    IscrizioneREA_StatoLiquidazione: {
        ctl_src: '1_2_4_5_StatoLiquidazione',
        ctl_dest: '',
        addr_book: '',
        anag: 'StatoLiquidazione',
    },

    //Contatti
    Contatti_Telefono: {
        ctl_src: '1_2_5_1_Telefono',
        ctl_dest: '1_4_2_cellulare',
        addr_book: 'cellulare',
        anag: 'Telefono',
    },
    Contatti_Fax: {
        ctl_src: '1_2_5_2_Fax',
        ctl_dest: '',
        addr_book: '',
        anag: 'Fax',
    },
    Contatti_Email: {
        ctl_src: '1_2_5_3_Email',
        ctl_dest: '1_4_2_email',
        addr_book: 'email',
        anag: 'Email',
    },

    RiferimentoAmministrazione: {
        ctl_src: '1_2_6_RiferimentoAmministrazione',
        ctl_dest: '',
        addr_book: '',
        anag: 'RiferimentoAmministrazione',
    },

    //StabileOrganizzazione
    //also checkbox
    StabileOrganizzazioneCheckbox: {
        ctl_src: '1_2_3_StabileOrganizzazioneCheckbox',
        ctl_dest: '1_4_3_StabileOrganizzazioneCheckbox',
        addr_book: '',
        anag: '',
    },
    StabileOrganizzazione_Indirizzo: {
        ctl_src: '1_2_3_1_Indirizzo',
        ctl_dest: '1_4_3_1_Indirizzo',
        addr_book: '',
        anag: '',
    },
    StabileOrganizzazione_NumeroCivico: {
        ctl_src: '1_2_3_2_NumeroCivico',
        ctl_dest: '1_4_3_2_NumeroCivico',
        addr_book: '',
        anag: '',
    },
    StabileOrganizzazione_CAP: {
        ctl_src: '1_2_3_3_CAP',
        ctl_dest: '1_4_3_3_CAP',
        addr_book: '',
        anag: '',
    },
    StabileOrganizzazione_Comune: {
        ctl_src: '1_2_3_4_Comune',
        ctl_dest: '1_4_3_4_Comune',
        addr_book: '',
        anag: '',
    },
    StabileOrganizzazione_Provincia: {
        ctl_src: '1_2_3_5_Provincia',
        ctl_dest: '1_4_3_5_Provincia',
        addr_book: '',
        anag: '',
    },
    StabileOrganizzazione_Nazione: {
        ctl_src: '1_2_3_6_Nazione',
        ctl_dest: '1_4_3_6_Nazione',
        addr_book: '',
        anag: '',
    },

    //RappresentanteFiscale
    //also checkbox
    RappresentanteFiscaleCheckbox: {
        ctl_src: '1_3_RappresentanteFiscaleCheckbox',
        ctl_dest: '1_4_4_RappresentanteFiscaleCheckbox',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_IdPaese: {
        ctl_src: '1_3_1_1_1_IdPaese',
        ctl_dest: '1_4_4_1_1_IdPaese',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_IdCodice: {
        ctl_src: '1_3_1_1_2_IdCodice',
        ctl_dest: '1_4_4_1_2_IdCodice',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_CodiceFiscale: {
        ctl_src: '1_3_1_2_CodiceFiscale',
        ctl_dest: '1_4_4_CodiceFiscale',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_Denominazione: {
        ctl_src: '1_3_1_3_1_Denominazione',
        ctl_dest: '1_4_4_2_Denominazione',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_Nome: {
        ctl_src: '1_3_1_3_2_Nome',
        ctl_dest: '1_4_4_3_Nome',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_Cognome: {
        ctl_src: '1_3_1_3_3_Cognome',
        ctl_dest: '1_4_4_4_Cognome',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_Titolo: {
        ctl_src: '1_3_1_3_4_Titolo',
        ctl_dest: '1_4_4_Titolo',
        addr_book: '',
        anag: '',
    },
    RappresentanteFiscale_CodEORI: {
        ctl_src: '1_3_1_3_5_CodEORI',
        ctl_dest: '1_4_4_CodEORI',
        addr_book: '',
        anag: '',
    },
} as const satisfies Record<string, AnagraficaTableRow>;

const ANAGRAFICA_TABLE = _ANAGRAFICA_TABLE as Record<string, AnagraficaTableRow>;

type UniversalAnagraficaKey = keyof typeof _ANAGRAFICA_TABLE;
type DefType = keyof AnagraficaTableRow | 'universal';
type UniversalAnagrafica = Partial<Record<UniversalAnagraficaKey, ProbablyString>>;
type Converter = (value: unknown) => ProbablyString;

function asNullableTextConverter(value: unknown) {
    if(value === null || value === undefined || value === '') return null;
    if(typeof value === 'string') return value;
    if(typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
}

function asUndefinedTextConverter(value: unknown) {
    return asNullableTextConverter(value) ?? undefined;
}

function asTextConverter(value: unknown) {
    return asNullableTextConverter(value) ?? '';
}

//universal converter
function convertTo(from: Record<string, unknown>, fromType: DefType, toType: DefType, converter?: Converter) {
    const to: Record<string, ProbablyString> = {};
    const fromUniversal = fromType === 'universal';
    const toUniversal = toType === 'universal';
    const _converter = converter ?? asNullableTextConverter;
    for(const [uname, entry] of Object.entries(ANAGRAFICA_TABLE)) {
        const fromName = fromUniversal ? uname : entry[fromType];
        const toName = toUniversal ? uname : entry[toType];
        if(!fromName || !toName) continue;

        const value = from[fromName];
        to[toName] = _converter(value);
    }
    return to;
}

function toUniversalData(from: Record<string, unknown>, fromType: DefType, converter?: Converter) {
    return convertTo(from, fromType, 'universal', converter) as UniversalAnagrafica;
}

function toCedentePrestatoreData(from: Record<string, unknown>, fromType: DefType, converter?: Converter) {
    return convertTo(from, fromType, 'ctl_src', converter);
}

function toCessionarioCommittenteData(from: Record<string, unknown>, fromType: DefType, converter?: Converter) {
    return convertTo(from, fromType, 'ctl_dest', converter);
}

function toAnagraficaData(from: Record<string, unknown>, fromType: DefType, converter?: Converter) {
    return convertTo(from, fromType, 'anag', converter) as FeAnagrafica_FeAnagrafiche;
}

function toAddressBookData(from: Record<string, unknown>, fromType: DefType, converter?: Converter) {
    return convertTo(from, fromType, 'addr_book', converter) as FeFatturaSavedData_FeFatturaSavedAddressBook;
}

export {
    asNullableTextConverter,
    asTextConverter,
    asUndefinedTextConverter,
    toAddressBookData,
    toAnagraficaData,
    toCedentePrestatoreData,
    toCessionarioCommittenteData,
    toUniversalData,
};
